import { Box, Button, Stack } from "@mui/material";
import { type FC, useMemo } from "react";
import {
	type PinFutureContractFormValues,
	usePinFutureContractForm,
} from "web_myplann/Goals/Hooks/usePinFutureContractForm";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

export type { PinFutureContractFormValues };

type Props = {
	open: boolean;
	onClose: () => void;
	onSubmit: (values: PinFutureContractFormValues) => void;
};

export const PinFutureContractDrawer: FC<Props> = ({
	open,
	onClose,
	onSubmit,
}) => {
	const { t } = useResource();
	const { form, typeOptions, isLoanType, canSubmit } =
		usePinFutureContractForm();
	const { control, handleSubmit } = form;

	const now = useMemo(() => new Date(), []);

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(Resources.Goals.Detail.Solution.Pin.Future.title)}
			closeButton
		>
			<Stack component="form" onSubmit={handleSubmit(onSubmit)} pb={2}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Goals.Detail.Solution.Pin.Future.nameLabel)}
					placeholder={t(
						Resources.Goals.Detail.Solution.Pin.Future.namePlaceholder,
					)}
				/>
				<FlatRowFormSelect
					control={control}
					name="type"
					label={t(Resources.Goals.Detail.Solution.Pin.Future.typeLabel)}
					options={typeOptions}
				/>
				{isLoanType ? (
					<FlatRowFormInput
						control={control}
						name="loanAmount"
						type="number"
						label={t(
							Resources.Goals.Detail.Solution.Pin.Future.loanAmountLabel,
						)}
						placeholder={t(
							Resources.Goals.Detail.Solution.Pin.Future.loanAmountPlaceholder,
						)}
						suffix={t(Resources.Goals.Amount.currencySign)}
					/>
				) : (
					<>
						<FlatRowFormInput
							control={control}
							name="lumpSum"
							type="number"
							label={t(Resources.Goals.Detail.Solution.Pin.Future.lumpSumLabel)}
							placeholder={t(
								Resources.Goals.Detail.Solution.Pin.Future.lumpSumPlaceholder,
							)}
							suffix={t(Resources.Goals.Amount.currencySign)}
						/>
						<FlatRowFormInput
							control={control}
							name="monthly"
							type="number"
							label={t(Resources.Goals.Detail.Solution.Pin.Future.monthlyLabel)}
							placeholder={t(
								Resources.Goals.Detail.Solution.Pin.Future.monthlyPlaceholder,
							)}
							suffix={t(Resources.Goals.Amount.currencySign)}
						/>
						<FlatRowFormDatePicker
							control={control}
							name="startDate"
							label={t(
								Resources.Goals.Detail.Solution.Pin.Future.startMonthLabel,
							)}
							views={["year", "month"]}
							minDate={now}
						/>
					</>
				)}
				<FlatRowFormInput
					control={control}
					name="note"
					label={t(Resources.Goals.Detail.Solution.Pin.Future.noteLabel)}
					placeholder={t(
						Resources.Goals.Detail.Solution.Pin.Future.notePlaceholder,
					)}
					isLast
				/>
				<Box px={2} pt={2}>
					<Button type="submit" variant="primary" disabled={!canSubmit}>
						{t(Resources.Goals.Detail.Solution.Pin.Future.save)}
					</Button>
				</Box>
			</Stack>
		</MyPlannDrawer>
	);
};
